import type { MaybeComputedElementRef } from "@vueuse/core";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type { IToC } from "~/models/IToC";

export function paragraphInViewObserver(
  elements: MaybeRefOrGetter<Element[]>,
  legalTextSlug: MaybeRefOrGetter<string>,
  allItems: MaybeRefOrGetter<(ILegalTextParagraph | IToC)[]>,
  parentRef: MaybeComputedElementRef,
  scrollingEnabled: MaybeRefOrGetter<boolean>
) {
  const paragraphInView = ref<ILegalTextParagraph>();
  const navigate = useNavigate();

  const { href } = useParagraphLink({
    legalTextSlug,
    paragraphSlug: () => paragraphInView.value?.slug,
    keepNote: true,
  });

  watch(paragraphInView, (p, oldP) => {
    if (!p || !oldP || p.id === oldP.id || !toValue(scrollingEnabled)) {
      return;
    }

    return navigate(href.value);
  });

  const sortedElementsByIndex = computed(() =>
    [...toValue(elements)].sort((a, b) => {
      const aIndex = parseInt(a?.getAttribute("data-index") ?? "-1");
      const bIndex = parseInt(b?.getAttribute("data-index") ?? "-1");
      return aIndex - bIndex;
    })
  );

  watch(sortedElementsByIndex, async (els) => {
    if (!toValue(scrollingEnabled)) {
      return;
    }
    await nextTick();
    const parentRect = unrefElement(parentRef)?.getBoundingClientRect();
    if (!parentRect || els.length === 0) {
      return;
    }
    // find the one that touches the parentRef's top
    const el = els.findLast((el) => {
      const rect = el.getBoundingClientRect();
      return rect.top < parentRect.top + 10;
    });

    if (!el) {
      paragraphInView.value = undefined;
      return;
    }

    const index = parseInt(el.getAttribute("data-index") ?? "-1");
    if (index === -1) {
      paragraphInView.value = undefined;
      return;
    }

    const newP = toValue(allItems)[index];
    if ("paragraph" in newP && newP.id !== paragraphInView.value?.id) {
      paragraphInView.value = newP;
    }
  });
}
